/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import _forEach from 'lodash/forEach';
import bnd3Service from '../../../services/bnd3.service';
import xAxisGridService from './xAxis.service';
import gridService from './grid.service';
import { is_iphone } from '../../../services/utils/is_iphone';
import { is_ipad } from '../../../services/utils/is_ipad';
import utilityService from '../../../services/utility.service';
import ChartConst from '../../../constants/chart.constant';
import AppConst from '../../../constants/app.constant';
import d3 from '../../../globalexports/d3.export';
import { initGridCanvas } from './canvas/inititalizeCanvas';
import { drawWeathersImages } from './canvas/drawWeatherImages';
import { roundOfNumber } from '../../../services/utils/roundOfNumber';
import * as LayerUpdateTimeService from '../../../services/layerUpdateTime/layerUpdateTime.service';

const gridViewChartService = () => {
  const content = {};
  let squareWidth;
  let squareHeight;
  let airportSqaureWidth;
  let zuluTextWidth;
  let normalTextHeight;
  content.chartApi = {};
  content.chartData = null;
  content.completeData = null;
  content.chartAloftData = null;
  content.chartNoDataForAloftAvailable = null;
  content.settings = null;
  content.trackRouteObject = null;

  content.xAxisObject = null;
  content.xAxisObjects = {};
  content.gridMapObj = [];
  content.getNoDataMessage = () => ChartConst.noDataFoundRouteMessage;
  content.showHideNoData = (show) => {
    if (show) {
      d3.select('#gridviewNoDataMessage').style('display', 'block');
      d3.select('#gridviewNoDataMessage span').html(content.noDataFoundText);
    } else {
      d3.select('#gridviewNoDataMessage').style('display', 'none');
    }
  };
  content.cleanGridViewChart = () => {
    d3.selectAll('#gridViewChartContainer svg').remove();
  };
  content.showNoDataStrip = (fullData, settings) => {
    content.noDataFoundText = content.getNoDataMessage(fullData, settings);
    content.showHideNoData(true);
    content.cleanGridViewChart();
  };
  content.init = (sourceData) => {
    let margin;
    let xAxisTopContainerY;
    let dateFormat;
    let dateFormat2;
    // var zuluTextWidth;
    const airportBoxExtraSpace = 2;
    const { xAxisAndChartDistance } = ChartConst;
    let sliderHeight;
    const hideBottomAxis = utilityService.hideBottomAxis();
    content.gridMapObj = [];
    if (content.settings.timeZone === AppConst.timeZones.local.key) {
      dateFormat = d3.timeFormat(ChartConst.GraphConfig.xAxis.format + ChartConst.GraphConfig
        .xAxis.timePostFix.local);
      dateFormat2 = d3.timeFormat(ChartConst.GraphConfig.xAxis2.format);
      // sourceData.forecast = bnChartDataProcessService.parseDatesInMode(
      // sourceData.forecast, ChartConst.dateMode.local);
    } else {
      dateFormat = d3.utcFormat(ChartConst.GraphConfig.xAxis.format + ChartConst.GraphConfig
        .xAxis.timePostFix.zulu);
      dateFormat2 = d3.utcFormat(ChartConst.GraphConfig.xAxis2.format);
      // sourceData.forecast = bnChartDataProcessService.parseDatesInMode(
      // sourceData.forecast, ChartConst.dateMode.local);
    }
    _forEach(sourceData, (item) => {
      if (content.settings.temperatureUnit === AppConst.temperatureUnits.fahrenheit.key) {
        item.tmp_display_value = item.tmp_fahrenheit;
      } else {
        item.tmp_display_value = item.tmp_celsius;
      }
    });

    margin = is_iphone()
      ? ChartConst.GraphConfig.marginMobile
      : ChartConst.GraphConfig.margin;
    margin = is_ipad()
      ? ChartConst.GraphConfig.marginIPad
      : margin;
    margin.left = utilityService.getSafeAreaPadding().left;
    margin.right = utilityService.getSafeAreaPadding().right;
    if (hideBottomAxis) {
      margin.right += utilityService.getSliderHeight();
    }
    if (!sourceData) {
      return;
    }
    sliderHeight = utilityService.getFooterHeight() + utilityService.iPhoneXFooterExtraOffset();
    if (!hideBottomAxis && content.settings.sliderPinned) {
      sliderHeight += utilityService.getSliderHeight();
    }
    const svgWidth = window.innerWidth - (utilityService.is_small_device()
      ? 0
      : parseFloat(AppConst.sideMenuWidth));
    const svgHeight = window.innerHeight - utilityService.getHeaderHeight();
    const width = svgWidth - margin.left - margin.right;
    const majorContainerHeight = svgHeight - sliderHeight;
    // Creating SVG Container Element
    const svg = d3
      .select('#gridViewChartContainer')
      .append('svg')
      .attr('preserveAspectRatio', 'xMinYMin meet')
      .attr('viewBox', `0 0 ${svgWidth} ${svgHeight}`) // class to make it responsive
      .classed('svg-content-responsive', true);
    d3.select('#gridViewChartContainer').style('height', `${svgHeight - sliderHeight}px`);
    const bncharts = svg.append('g').classed('bnchartsgrid', true);
    bncharts.append('g').attr('id', 'main-container-grid').append('rect')
      .attr('class', 'major-box')
      .attr('x', margin.left)
      .attr('y', 0)
      .attr('width', width)
      .attr('height', majorContainerHeight);
    bncharts.append('text').text('Bn Chart Reference').attr('id', 'bntextrefgrid').attr(
      'class',
      'ref-text',
    );
    bncharts.append('text').text('77% RLH').attr('id', 'bn-overlap-textref-grid').attr(
      'class',
      'ref-text',
    );
    bncharts.append('text').text('24Z').attr('id', 'bnZuluTextRefGrid').attr(
      'class',
      'ref-text',
    );
    bncharts.append('text').text('KMGW').attr('id', 'bnAirportTextRefGrid').attr(
      'class',
      'ref-text',
    );
    normalTextHeight = bnd3Service.getSelectionHeight(d3.select('#bntextrefgrid'));
    zuluTextWidth = bnd3Service.getSelectionWidth(d3.select('#bnZuluTextRefGrid'));
    const airportTextWidth = bnd3Service.getSelectionWidth(d3.select('#bnAirportTextRefGrid'));
    const xAxisTopContainerHeight = (normalTextHeight * 2) + xAxisAndChartDistance;
    const grigAreaRulesHeight = majorContainerHeight - (xAxisTopContainerHeight * 2);

    airportSqaureWidth = roundOfNumber(airportTextWidth + airportBoxExtraSpace);

    /* Adding extra record to draw Airport BOX */
    _forEach(sourceData, (item) => {
      if (item.forecast && item.forecast[0]) {
        item.forecast.unshift(item.forecast[0]);
      }
    });
    squareWidth = roundOfNumber(((width - airportSqaureWidth) / (sourceData[0].forecast
      .length - 1)));
    squareHeight = roundOfNumber(grigAreaRulesHeight / sourceData.length);
    xAxisTopContainerY = normalTextHeight;
    const xAxisTopContainer = bncharts.append('g')
      .attr('id', 'x-axis-strip-top-grid')
      .attr('transform', () => {
        const x = margin.left + airportSqaureWidth;
        const y = xAxisTopContainerY;
        return `translate(${x},${y})`;
      });
    xAxisTopContainerY = grigAreaRulesHeight + xAxisTopContainerHeight + normalTextHeight;
    const xAxisBottomContainer = bncharts
      .append('g')
      .attr('id', 'x-axis-strip-bottom-grid')
      .attr('transform', () => {
        const x = margin.left + airportSqaureWidth;
        const y = xAxisTopContainerY;
        return `translate(${x},${y})`;
      });
    const gridAreaRegionY = xAxisTopContainerHeight;
    const gridAreaRegion = bncharts.append('g')
      .attr('id', 'grid-area-region')
      .attr('transform', () => `translate(${margin.left}, ${gridAreaRegionY})`);
    initGridCanvas(width, majorContainerHeight, margin.left, gridAreaRegionY);
    // grigAreaRulesHeight + xAxisTopContainerHeight + xAxisTopContainerHeight + normalTextHeight;
    const gridChartAxis = (
      content.settings.timeZone === 'L'
        ? d3.scaleTime().range([airportSqaureWidth, width]).unknown(airportSqaureWidth)
        : d3.scaleUtc().range([airportSqaureWidth, width]).unknown(airportSqaureWidth)
    ).unknown(airportSqaureWidth);

    if (d3.select('#main-container-grid').empty() === false) {
      d3.select('#main-container-grid')
        .append('rect')
        .attr({
          width: airportSqaureWidth,
          height: majorContainerHeight,
          fill: ChartConst.gridView.airportRectColor,
          transform: `translate(${margin.left} , 0)`,
          stroke: ChartConst.gridView.borderColor,
          'stroke-width': ChartConst.gridView.borderWidth,
        });
    }
    const travelAxis = content.settings.timeZone === 'L'
      ? d3
        .scaleTime()
        .range([airportSqaureWidth, width - squareWidth])
        .unknown(airportSqaureWidth)
      : d3
        .scaleUtc()
        .range([airportSqaureWidth, width - squareWidth])
        .unknown(airportSqaureWidth);
    // Draw x-axis top
    content.xAxisObjects.top = xAxisTopContainer;
    content.xAxisObjects.bottom = xAxisBottomContainer;
    content.xAxisObject = [
      sourceData,
      content.xAxisObjects.top,
      width,
      gridChartAxis,
      dateFormat,
      dateFormat2,
      content.settings,
      airportSqaureWidth,
      squareWidth,
      xAxisTopContainerHeight,
      travelAxis,
      majorContainerHeight,
      normalTextHeight,
      zuluTextWidth,
      true,
    ];
    xAxisGridService.drawXaxis.apply(null, content.xAxisObject);
    content.xAxisObject[content.xAxisObject.length - 1] = false;
    content.xAxisObject[1] = content.xAxisObjects.bottom;
    // Draw x-axis bottom
    xAxisGridService.drawXaxis.apply(null, content.xAxisObject);
    drawWeathersImages(
      squareWidth,
      squareHeight,
      airportSqaureWidth,
      sourceData,
    );
    content.trackRouteObject = gridService.drawChart(
      sourceData,
      sourceData,
      gridAreaRegion,
      content.settings,
      airportSqaureWidth,
      squareWidth,
      squareHeight,
      content.fullData,
      width,
      travelAxis,
      content.settings,
    );
  };
  content.processData = (data) => {
    if (data.length) {
      content.showHideNoData(false);
      content.fullData = data;
      content.chartData = data.gridDataObject;
      content.init(utilityService.clone(content.chartData), data);
    } else {
      content.showHideNoData(true);
    }
  };

  function setNewColorAndImage(rowObj, d, j) {
    if (rowObj.cols[j]) {
      rowObj.cols[j].rect.attr('fill', bnd3Service.getFlightDotColor(d.forecast[j].frl));
    }
  }

  function softdrawGridSquare(mapObj) {
    let i = 0;
    let j;
    while (i < content.chartData.length) {
      j = 0;
      while (j < content.chartData[i].forecast.length) {
        setNewColorAndImage(mapObj[i], content.chartData[i], j);
        j += 1;
      }
      i += 1;
    }
  }

  function getCols(row, d, j) {
    const cols = {
      rect: row.select(`#col-${d.icao_id}-${j + 1}`),
      img: row.select(`#col-img-${d.icao_id}-${j + 1}`),
    };
    return cols;
  }

  function buildGridMap() {
    let i = 0;
    let j;
    let row;
    let cols;
    let rowcolobj;
    if (content.chartData) {
      while (i < content.chartData.length) {
        j = 0;
        row = d3.select(`#gird-row-${content.chartData[i].icao_id}`);
        rowcolobj = {
          cols: [],
        };
        while (j < content.chartData[i].forecast.length) {
          cols = getCols(row, content.chartData[i], j);
          rowcolobj.cols.push(cols);
          j += 1;
        }
        content.gridMapObj.push(rowcolobj);
        i += 1;
      }
    }
  }

  function softDrawGrid(data) {
    content.fullData = data;
    content.chartData = data.gridDataObject;
    if (!content.chartData) {
      return;
    }
    if (content.gridMapObj.length === 0) {
      buildGridMap();
    }
    // d3.select('#d3tiprootelementgrid').style('visibility', 'hidden');
    const chartData2 = data.gridDataObject.map((item) => {
      const fc = item.forecast.map(({
        forecast_ts,
        forecast_ts_local,
        frl,
        sunMoonPositionData,
        weatherRepresentation,
      }) => ({
        forecast_ts,
        forecast_ts_local,
        frl,
        lat: item.lat,
        lng: item.lng,
        sunMoonPositionData,
        weatherRepresentation,
      }));

      return ({
        icao_id: item.icao_id,
        lat: item.lat,
        lng: item.lng,
        total_nm: item.icao_id,
        forecast: fc,
      });
    });
    /* Adding extra record to draw Airport BOX */
    _forEach(chartData2, (item) => {
      if (item.forecast && item.forecast[0]) {
        item.forecast.unshift(item.forecast[0]);
      }
    });
    content.trackRouteObject[1] = content.fullData.travelRoute;
    softdrawGridSquare(content.gridMapObj);
    drawWeathersImages(
      squareWidth,
      squareHeight,
      airportSqaureWidth,
      chartData2,
    );
    content.xAxisObjects.top.selectAll('*').remove();
    content.xAxisObjects.bottom.selectAll('*').remove();
    content.xAxisObject[0] = chartData2;
    content.xAxisObject[content.xAxisObject.length - 1] = true;
    content.xAxisObject[1] = content.xAxisObjects.top;
    xAxisGridService.drawXaxis.apply(null, content.xAxisObject);
    content.xAxisObject[content.xAxisObject.length - 1] = false;
    content.xAxisObject[1] = content.xAxisObjects.bottom;
    content.trackRouteObject[2].domain(d3.extent(chartData2[0].forecast, (d) => d
      .forecast_ts));
    xAxisGridService.drawXaxis.apply(null, content.xAxisObject);
    gridService.drawTravelRouteSoft.apply(null, content.trackRouteObject);
  }
  const doSoftDrawGrid = (data) => {
    window.requestAnimationFrame(() => {
      softDrawGrid(data);
    });
  };
  content.startRendering = (data) => {
    if (data) {
      if (!content.softDraw) {
        content.cleanGridViewChart();
        content.processData(data);
      } else {
        doSoftDrawGrid(data);
      }
    }
  };
  // Call this function too redraw the chart
  content.chartApi.refresh = (data, settings, softDrawValue) => {
    content.settings = settings;
    content.softDraw = softDrawValue;
    content.startRendering(data);
    LayerUpdateTimeService.setCurrentScreenRenderTime();
  };
  return content;
};
export default gridViewChartService();
